<template>
    <UPopover v-if="managers.length > 0" v-model:open="open" :disabled="!canOpenManagerMenu">
        <UButton
            color="neutral"
            variant="ghost"
            :trailing-icon="canOpenManagerMenu ? 'i-lucide-chevron-down' : undefined"
            :ui="{ base: 'max-w-72 sm:border sm:border-default sm:bg-default sm:shadow-xs', trailingIcon: 'text-primary size-4 stroke-[3]' }"
        >
            <AppIcon name="home" class="size-9" />
            <span class="hidden min-w-0 flex-col text-left sm:flex">
                <span class="text-[0.65rem] leading-none tracking-wide text-muted uppercase">
                    {{ $t('manager.label') }}
                </span>
                <span class="line-clamp-2 text-sm leading-4 font-semibold">
                    {{ displayManager }}
                </span>
            </span>
        </UButton>

        <template #content>
            <div class="w-80 max-w-[calc(100vw-2rem)] p-2">
                <UInput
                    v-if="showSearch"
                    v-model="searchManagersValue"
                    class="mb-2 w-full"
                    size="sm"
                    icon="i-lucide-search"
                    :placeholder="$t('manager.search')"
                    @update:model-value="searchManagers"
                />

                <ul class="flex max-h-72 flex-col gap-0.5 overflow-auto">
                    <li v-for="manager in managersToDisplay" :key="manager.manager">
                        <button
                            type="button"
                            class="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-elevated"
                            :class="{ 'bg-primary/10 font-semibold': isSelected(manager) }"
                            @click="onSelectManager(manager.manager)"
                        >
                            <span class="min-w-0 flex-1">{{ manager.full_title }}</span>
                            <UIcon v-if="isSelected(manager)" name="i-lucide-check" class="size-4 shrink-0 text-primary" />
                        </button>
                    </li>
                    <li v-if="managersToDisplay.length === 0" class="px-3 py-2 text-sm text-muted">
                        {{ $t('manager.no_results') }}
                    </li>
                </ul>
            </div>
        </template>
    </UPopover>
</template>

<script setup lang="ts">
    import AppIcon from '@/components/AppIcon.vue'
    import { computed, onMounted, ref, type Ref, toRaw, watch } from 'vue'
    import { useI18n } from 'vue-i18n'

    import { parseError } from '@/libs/error'
    import { PrintError, PrintSuccess } from '@/libs/toast'
    import { useAppStore } from '@/stores/app'
    import { useFetchStore } from '@/stores/fetch.ts'
    import type { Manager } from '@/types/Manager'
    import type { UserData } from '@/types/User'

    // config
    const SEARCH_THRESHOLD = 7

    // data
    const { t } = useI18n()
    const appStore = useAppStore()
    const fetchStore = useFetchStore()

    const managers: Ref<Manager[]> = ref([])
    const selectedManager: Ref<string> = ref('')

    const filteredManagers: Ref<Manager[]> = ref([])
    const searchManagersValue: Ref<string> = ref('')
    let searchMangersTimeout: ReturnType<typeof setTimeout> | undefined

    const open: Ref<boolean> = ref(false)

    // computed
    const userData = computed(() => {
        return appStore.getUserData()
    })

    const loggedIn = computed(() => {
        return appStore.getUserLoggedIn()
    })

    const displayManager = computed(() => {
        if (managers.value.length > 0) {
            const index = managers.value.findIndex(x => x.manager === selectedManager.value)
            return index > -1 && managers.value[index] ? managers.value[index].full_title : '/'
        }
        return '/'
    })

    const managersToDisplay = computed(() => {
        const base = searchManagersValue.value ? filteredManagers.value : managers.value
        const sel = selectedManager.value

        let ordered = base
        if (sel) {
            const idx = base.findIndex(m => m.manager === sel)
            if (idx > 0) {
                ordered = [...base]
                const [item] = ordered.splice(idx, 1)
                if (item) ordered.unshift(item)
            }
        }

        return ordered
    })

    const canOpenManagerMenu = computed(() => {
        return managers.value.length > 1
    })

    const showSearch = computed(() => {
        return managers.value.length > SEARCH_THRESHOLD
    })

    // methods
    const isSelected = (manager: Manager) => {
        return selectedManager.value === manager.manager
    }

    const loadData = async() => {
        if (!loggedIn.value) return

        const { response, data } = await fetchStore.fetch(`/user_managers`).get().json<Manager[]>()
        if (response.value?.status === 200 && data.value) {
            managers.value = data.value
            managers.value = managers.value.sort((a, b) => a.full_title.toLowerCase().localeCompare(b.full_title.toLowerCase()))

            filteredManagers.value = structuredClone(toRaw(managers.value))
            if (userData.value.selected_manager) selectedManager.value = userData.value.selected_manager

            const index = managers.value.findIndex(x => x.manager === selectedManager.value)
            if (index > -1) {
                const [item] = managers.value.splice(index, 1)
                if (item) managers.value.unshift(item)
            }
        } else {
            managers.value = []
            filteredManagers.value = []
            selectedManager.value = ''
            PrintError(parseError(data.value, t('errors.load_data')))
        }
    }

    const onSelectManager = async(selectedValue: string) => {
        const index = managers.value.findIndex(x => x.manager === selectedValue)
        if (index < 0 || selectedManager.value === selectedValue || !managers.value[index]) return

        open.value = false
        appStore.startLoading()

        const { response, data } = await fetchStore.fetch(`/switch_manager/${managers.value[index].manager}`).post().json<UserData>()
        if (response.value?.status === 200 && data.value) {
            appStore.setLogin(data.value, data.value.token)
            PrintSuccess(t('manager.switch_success'))
            window.location.reload()
        } else {
            PrintError(parseError(data.value, t('manager.switch_error')))
        }

        appStore.stopLoading()
    }

    const searchManagers = () => {
        if (searchManagersValue.value.length === 0) {
            filteredManagers.value = structuredClone(toRaw(managers.value))
            return
        }
        if (searchMangersTimeout) clearTimeout(searchMangersTimeout)

        searchMangersTimeout = setTimeout(() => {
            filteredManagers.value = managers.value.filter(x => x.full_title.toLowerCase().indexOf(searchManagersValue.value.toLowerCase()) !== -1)
        }, 200)
    }

    // watcher
    watch(open, (isOpen) => {
        if (isOpen) searchManagersValue.value = ''
    })

    // hook
    onMounted(async() => {
        await loadData()
    })
</script>
